/**
 * RBAC (Role-Based Access Control) Utilities
 *
 * Comprehensive utilities for implementing and enforcing RBAC
 * in the Admin Frontend application.
 */

import React from 'react';

import Forbidden from '../components/common/Forbidden';
import {
  ROLES,
  ROLE_LEVELS,
  PERMISSIONS,
  PERMISSION_CATEGORIES,
  getRolePermissions,
  getRoleLevel,
  hasMinimumRole,
  normalizeRole,
  userHasPermission,
  userHasAnyPermission,
  userHasAllPermissions,
  getUserPermissions,
  getUserPermissionsByCategory,
} from '../constants/permissions';
import { useAuth } from '../contexts/AuthContext';

/**
 * RBAC Manager Class
 * Provides centralized RBAC functionality
 */
export class RBACManager {
  constructor(user) {
    this.user = user;
    this.userPermissions = getUserPermissions(user);
  }

  /**
   * Update user for permission checks
   * @param {Object} user - New user object
   */
  setUser(user) {
    this.user = user;
    this.userPermissions = getUserPermissions(user);
  }

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean}
   */
  hasPermission(permission) {
    return userHasPermission(this.user, permission);
  }

  /**
   * Check if user has any of the specified permissions
   * @param {string[]} permissions - Array of permissions
   * @returns {boolean}
   */
  hasAnyPermission(permissions) {
    return userHasAnyPermission(this.user, permissions);
  }

  /**
   * Check if user has all of the specified permissions
   * @param {string[]} permissions - Array of permissions
   * @returns {boolean}
   */
  hasAllPermissions(permissions) {
    return userHasAllPermissions(this.user, permissions);
  }

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean}
   */
  hasRole(role) {
    if (!this.user) return false;
    const userRoles = this.user.roles?.length
      ? this.user.roles
      : [this.user.role];
    return userRoles.some(r => r && normalizeRole(r) === role);
  }

  /**
   * Check if user has minimum role level
   * @param {string} minRole - Minimum required role
   * @returns {boolean}
   */
  hasMinimumRole(minRole) {
    if (!this.user) return false;

    // Check roles array
    if (this.user.roles) {
      return this.user.roles.some(role => hasMinimumRole(role, minRole));
    }

    // Check single role (legacy)
    if (this.user.role) {
      return hasMinimumRole(this.user.role, minRole);
    }

    return false;
  }

  /**
   * Get all user permissions
   * @returns {string[]}
   */
  getPermissions() {
    return this.userPermissions;
  }

  /**
   * Get permissions by category
   * @param {string} category - Permission category
   * @returns {string[]}
   */
  getPermissionsByCategory(category) {
    return getUserPermissionsByCategory(this.user, category);
  }

  /**
   * Check access to a resource/action combination
   * @param {string} resource - Resource name (e.g., 'user', 'kyc', 'camera')
   * @param {string} action - Action name (e.g., 'read', 'write', 'delete')
   * @returns {boolean}
   */
  canAccess(resource, action) {
    const permission = `${resource}:${action}`;
    return this.hasPermission(permission);
  }

  /**
   * Check if user can manage another user
   * @param {Object} targetUser - User to check management permissions for
   * @returns {boolean}
   */
  canManageUser(targetUser) {
    if (!this.user || !targetUser) return false;

    // The backend lets only admins write to /api/v1/users. Admins manage
    // everyone except other admins.
    if (!this.hasRole(ROLES.ADMIN)) return false;
    const targetRoles = targetUser.roles?.length
      ? targetUser.roles
      : [targetUser.role];
    return !targetRoles.some(r => normalizeRole(r) === ROLES.ADMIN);
  }

  /**
   * Check if user can edit a specific resource
   * @param {string} resourceType - Type of resource ('user', 'camera', 'kyc', etc.)
   * @param {Object} resource - Resource object (optional, for ownership checks)
   * @returns {boolean}
   */
  canEdit(resourceType, resource = null) {
    // Check write permission for the resource type
    if (!this.canAccess(resourceType, 'write')) {
      return false;
    }

    // For user management, check additional rules
    if (resourceType === 'user' && resource) {
      return this.canManageUser(resource);
    }

    return true;
  }

  /**
   * Check if user can delete a specific resource
   * @param {string} resourceType - Type of resource
   * @param {Object} resource - Resource object (optional)
   * @returns {boolean}
   */
  canDelete(resourceType, resource = null) {
    // Check delete permission for the resource type
    if (!this.canAccess(resourceType, 'delete')) {
      return false;
    }

    // For user management, check additional rules
    if (resourceType === 'user' && resource) {
      return this.canManageUser(resource);
    }

    return true;
  }

  /**
   * Get user's highest role level
   * @returns {number}
   */
  getHighestRoleLevel() {
    if (!this.user) return 0;

    let maxLevel = 0;

    if (this.user.roles) {
      this.user.roles.forEach(role => {
        const level = getRoleLevel(role);
        maxLevel = Math.max(maxLevel, level);
      });
    } else if (this.user.role) {
      maxLevel = getRoleLevel(this.user.role);
    }

    return maxLevel;
  }

  /**
   * Get user's roles sorted by hierarchy
   * @returns {string[]}
   */
  getRolesByHierarchy() {
    if (!this.user) return [];

    const roles = this.user.roles || (this.user.role ? [this.user.role] : []);

    return roles.sort((a, b) => getRoleLevel(b) - getRoleLevel(a));
  }
}

/**
 * React Hook for RBAC
 * @param {Object} user - User object from auth context
 * @returns {RBACManager} RBAC manager instance
 */
export const useRBAC = user => {
  useRBAC.displayName = 'useRBAC';
  return new RBACManager(user);
};

/**
 * Higher-order component for permission-based rendering
 * @param {string|string[]} permissions - Required permission(s)
 * @param {React.Component} Fallback - Fallback component to render if no permission
 * @returns {function} HOC function
 */
export const withPermission = (permissions, Fallback = Forbidden) => {
  withPermission.displayName = 'withPermission';
  return Component => {
    const PermissionGuard = props => {
      const { user } = useAuth();
      const rbac = useRBAC(user);

      const requiredPerms = Array.isArray(permissions)
        ? permissions
        : [permissions];
      const hasAccess = rbac.hasAnyPermission(requiredPerms);

      if (!hasAccess) {
        return React.createElement(Fallback);
      }

      return React.createElement(Component, props);
    };
    PermissionGuard.displayName = 'PermissionGuard';
    return PermissionGuard;
  };
};

/**
 * Higher-order component for role-based rendering
 * @param {string|string[]} roles - Required role(s)
 * @param {boolean} requireAll - Whether all roles are required (default: false)
 * @param {React.Component} Fallback - Fallback component to render if no access
 * @returns {function} HOC function
 */
export const withRole = (roles, requireAll = false, Fallback = null) => {
  withRole.displayName = 'withRole';
  return Component => {
    const RoleGuard = props => {
      const { user } = useAuth();
      const rbac = useRBAC(user);

      const requiredRoles = Array.isArray(roles) ? roles : [roles];
      let hasAccess = false;

      if (requireAll) {
        hasAccess = requiredRoles.every(role => rbac.hasRole(role));
      } else {
        hasAccess = requiredRoles.some(role => rbac.hasRole(role));
      }

      if (!hasAccess) {
        return Fallback ? React.createElement(Fallback) : null;
      }

      return React.createElement(Component, props);
    };
    RoleGuard.displayName = 'RoleGuard';
    return RoleGuard;
  };
};

/**
 * Permission guard for API calls
 * Throws error if user doesn't have required permission
 * @param {Object} user - User object
 * @param {string|string[]} permissions - Required permission(s)
 * @throws {Error} If permission denied
 */
export const requirePermission = (user, permissions) => {
  requirePermission.displayName = 'requirePermission';
  const rbac = new RBACManager(user);
  const requiredPerms = Array.isArray(permissions)
    ? permissions
    : [permissions];

  if (!rbac.hasAnyPermission(requiredPerms)) {
    throw new Error(
      `Permission denied. Required: ${requiredPerms.join(' or ')}`
    );
  }
};

/**
 * Role guard for API calls
 * Throws error if user doesn't have required role
 * @param {Object} user - User object
 * @param {string|string[]} roles - Required role(s)
 * @param {boolean} requireAll - Whether all roles required
 * @throws {Error} If role access denied
 */
export const requireRole = (user, roles, requireAll = false) => {
  requireRole.displayName = 'requireRole';
  const rbac = new RBACManager(user);
  const requiredRoles = Array.isArray(roles) ? roles : [roles];

  let hasAccess = false;
  if (requireAll) {
    hasAccess = requiredRoles.every(role => rbac.hasRole(role));
  } else {
    hasAccess = requiredRoles.some(role => rbac.hasRole(role));
  }

  if (!hasAccess) {
    throw new Error(
      `Role access denied. Required: ${requiredRoles.join(requireAll ? ' and ' : ' or ')}`
    );
  }
};

// Export constants for convenience
export {
  ROLES,
  ROLE_LEVELS,
  PERMISSIONS,
  PERMISSION_CATEGORIES,
  getRolePermissions,
  getRoleLevel,
  hasMinimumRole,
  userHasPermission,
  userHasAnyPermission,
  userHasAllPermissions,
  getUserPermissions,
  getUserPermissionsByCategory,
};

// Default export
export default {
  RBACManager,
  useRBAC,
  withPermission,
  withRole,
  requirePermission,
  requireRole,
};
